<template>
    <div class="accommodation-content-wrapper">
        <div class="form-element pb-0 mb-3">
            <ContentHeadline v-if="step.firstQuestion">{{step.firstQuestion}}</ContentHeadline>
        </div>
        <div :key="999" v-if="step.categories.length > 1 && step.items.length === 0" class="form-element row">
          <div class="col-md-4 col-12" v-for="(category, index) in step.categories">
            <a href="#" :for="'cat-select-'+index"
               class="btn btn-outline-info col-12 mb-1 mb-md-0"
               :class="{active:step.catSelect === 'cat-select-'+index}"
               @click.prevent="addItem(index, true)"
            >
              {{category.title}}
            </a>
          </div>
        </div>
        <transition-group name="item-list" tag="div" class="items" v-if="step.items.length">
            <div data-index="index" :key="`key-${index}`" v-for="(item, index) in step.items"
                 class="form-element form-element-bg keep-padding">
                <div class="mb-3 d-flex">
                  <span class="numbering me-2">{{index +1}}.</span>
                  <select data-item="items"
                          required
                          class="form-control fw-bold form-select w-75"
                          @change="validateItems(item)"
                          v-model="item.category"
                          :class="{ 'is-invalid': item.errors.category }"
                          :disabled="parseInt(step.categories[item.listIndex].maxCount) === 1"
                          :aria-label="$t('form.hint.selectPrompt', {item: step.categories[item.listIndex].title })"
                  >
                      <option disable daria-hidden="true" value="">
                        -- {{ $t('form.hint.selectPrompt', {item: step.categories[item.listIndex].title }) }} --
                      </option>
                      <option v-for="category in step.categories[item.listIndex].options" :value=category>
                          {{category}}
                      </option>
                  </select>
                  <template v-if="parseInt(step.categories[item.listIndex].maxCount) !== 1">
                    <button @click.prevent="removeItem(index)" class="ms-2 btn btn-outline-danger text-nowrap">
                      <i :class="`${$store.state.faClass} fa-minus-circle me-1`" inline aria-hidden="true"></i>
                      <span class="d-none d-sm-inline">{{ $t('form.action.remove') }}</span>
                    </button>
                  </template>
                </div>
                <div v-if="hasSubitems(item.listIndex) && item.active" class="mb-3 ms-4">
                    <select data-item="items"
                            required
                            class="form-control col-11 col-md-8 fw-bold form-select"
                            @change="validateItems(item)"
                            v-model="item.subitem"
                            :class="{ 'is-invalid': item.errors.subitem }"
                            :aria-label="$t('form.hint.selectPrompt', {item: step.categories[item.listIndex].subitemTitle || '' })"
                    >
                        <option disabled aria-hidden="true" value="">-- {{ $t('form.hint.selectPrompt', {item: step.categories[item.listIndex].subitemTitle || '' }) }} --</option>
                        <option v-for="subitem in step.categories[item.listIndex].subitems" :value=subitem>
                            {{subitem}}
                        </option>
                    </select>
                </div>
                <div v-show="item.active">
                    <PeopleConfigurator :item="item"
                                        :index="index"
                                        :step="step"
                                        @changed="resetNextDisabledHint"
                                        ref="people"
                                        mandatory="true"
                    ></PeopleConfigurator>
                </div>
            </div>
        </transition-group>
        <div v-if="(step.items.length > 0 || step.categories.length === 1)" class="row form-element pt-0 mt-3">
          <template v-if="typeof step.categories[0].maxCount === 'undefined' || parseInt(step.categories[0].maxCount) < step.items.length">
            <div v-for="(item, index) in step.categories" class="add-item-js col-12 col-md-4 mb-3">
              <a class="w-100 h-100 btn btn-outline-info" href="#" @click.prevent="addItem(index)">
                <i :class="`${$store.state.faClass} fa-plus-circle me-1 icon`" inline aria-hidden="true"></i>
                {{ $t('form.action.add', {item: item.title.replace(' hinzufügen', '')}) }}
              </a>
            </div>
          </template>
        </div>

        <div v-if="step.services.length > 0" class="form-element mt-3">
            <div class="row">
                <ContentHeadline class="col-12 ch-accommodation-services" id="service-headline">
                  {{ $t('mod.accommodation.service.question') }}
                </ContentHeadline>
            </div>
            <div class="services row" role="group" aria-labelledby="service-headline">
                <div v-for="(service, index) in step.services" class="service-wrapper col-12 mb-3 col-md-4">
                    <div class="service noselect overflow-hidden h-100 m-1">
                        <input type="checkbox" v-model="step.userServices" :value="service.title"
                               :id="'service-'+index" class="btn-check">
                        <label :for="'service-'+index" class="h-100 m-0"
                               @keydown.enter="handleServiceToggle(service.title)"
                               @keydown.space="handleServiceToggle(service.title)"
                        >
                            <i class="d-block fa-3x" :class="service.faIcon" aria-hidden="true"></i>
                            <div class="text">{{service.title}}</div>
                            <div class="checked">
                              <i :class="`${$store.state.faClass} fa-check-circle fa-lg`" :aria-label="$t('form.hint.selected')" role="img"></i>
                            </div>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <div class="form-element">
            <ContentHeadline class="ch-accommodation-text" :id="createHeadlineId($t('mod.accommodation.message.headline'))">
              {{ $t('mod.accommodation.message.headline') }}
            </ContentHeadline>
            <div class="message">
                <textarea :placeholder="$t('mod.accommodation.message.placeholder')"
                          class="form-control" v-model="step.message" id="wuensche"
                          :aria-labelledby="createHeadlineId($t('mod.accommodation.message.headline'))"
                          rows="6" name="sonstige_wuensche"></textarea>
            </div>
        </div>
        <div class="form-group row justify-content-end mt-5 mb-4">
            <div class="col-12 col-md-4 d-none d-md-flex">
              <a href="#back" @click.prevent="clickPrev" class="btn-back mt-2 ms-4 pl-2" tabindex="0">
                &lt; {{ $t('form.action.back') }}
              </a>
            </div>
            <div class="col-md-4 d-none d-md-flex"></div>
            <div class="col-12 col-md-4">
                <div class="alert alert-warning" role="alert" v-show="showNextDisabledHint">
                  {{ $t('form.hint.nextDisabled') }}
                </div>
                <button type="button" @click="clickNext" class="btn btn-dark btn-block next">
                  {{ $t('form.action.next') }}
                </button>
            </div>
        </div>
    </div>
</template>
<script>
    import mixinCreateId from "../mixins/createId";
    import PeopleConfigurator from './partials/PeopleConfigurator';
    import ContentHeadline from "./partials/ContentHeadline.vue";

    export default {
        mixins: [mixinCreateId],
        props: ['step'],
        data() {
            return {
                showNextDisabledHint: false
            };
        },
        created() {
          if(this.step.categories.length === 1 && parseInt(this.step.categories[0].maxCount) === 1) {
            this.step.items[0].category = this.step.categories[0].options[0]
            this.step.items[0].active = true
          }
        },
        methods: {
            handleServiceToggle: function (serviceTitle) {
                if(this.step.userServices.includes(serviceTitle)) {
                    const index = this.step.userServices.indexOf(serviceTitle)
                    this.step.userServices.splice(index, 1)
                } else {
                    this.step.userServices.push(serviceTitle)
                }
            },
            resetNextDisabledHint: function() {
                this.showNextDisabledHint = false;
            },
            hasSubitems(index) {
                return typeof this.step.categories[index].subitems !== 'undefined';
            },
            validateItems(item) {
                this.showNextDisabledHint = false;

                // map => trigger all checks, every => ensure all passed
                const allGood = this.step.items.map(item => item.validate(this.step)).every(result => result);
                if (item) {
                    item.active = true;
                }
                this.step.configuratorActive = true;
                if(typeof this.$refs.people !=='undefined') {
                  for (let i = 0; i < this.$refs.people.length; i++) {
                    const peopleValid = this.$refs.people[i].validate();
                    if (!peopleValid) {
                      return false;
                    }
                  }
                }
                this.$forceUpdate();
                return allGood;
            },
            validate() {
                this.showNextDisabledHint = false;
                this.step.wasValidated = true;

                if (this.validateItems()) {
                    this.handleUserData();
                    return true
                }
                return false;
            },
            handleUserData() {
                this.showNextDisabledHint = false;
                this.step.clearSummary();
                let summaryText = '';
                for (let i = 0; i < this.step.items.length; i++) {
                    const item = this.step.items[i];
                    if (summaryText !== '') {
                        summaryText += '<br>';
                    }
                    summaryText += item.description();
                    this.$store.commit('updateUserInput', {
                        name: 'blocks[' + this.step.index + 100 + '][items][' + i + '][adults]',
                        module: this.step.type,
                        input: item.adults
                    });
                    this.$store.commit('updateUserInput', {
                        name: 'blocks[' + this.step.index + 100 + '][items][' + i + '][children]',
                        module: this.step.type,
                        input: item.children.length
                    });
                    let label = item.category;
                    if (this.hasSubitems(item.listIndex)) {
                        // this.$store.commit('updateUserInput', {
                        //     name: 'blocks[' + this.step.index + 100 + '][items][' + i + '][additional]',
                        //     module: this.step.type,
                        //     input: item.subitem
                        // });
                        label += ' ' + item.subitem;
                        summaryText += ' ' + item.subitem;
                    }

                    this.$store.commit('updateUserInput', {
                        name: 'blocks[' + this.step.index + 100 + '][items][' + i + '][label]',
                        module: this.step.type,
                        input: label
                    });

                    if (item.childAge !== '') {
                        this.$store.commit('updateUserInput', {
                            name: 'blocks[' + this.step.index + 100 + '][items][' + i + '][child_ages]',
                            module: this.step.type,
                            input: item.childAge
                        });
                    }
                }
                this.step.addToSummary({label: this.step.title, text: summaryText});
                if (this.step.userServices.length > 0) {
                    this.$store.commit('updateUserInput', {
                        name: 'service[]',
                        module: this.step.type,
                        input: this.step.userServices
                    });
                    this.$store.commit('updateUserInput', {
                        name: 'ii_service',
                        module: this.step.type,
                        input: 'Gewünschte Zusatzleistungen:'
                    });
                    this.step.addToSummary({
                      label: this.$t('mod.accommodation.service.question'),
                      text: this.step.userServices.join(", ")
                    })
                }
                if (this.step.message !== '') {
                    this.$store.commit('updateUserInput', {
                        name: 'sonstige_wuensche',
                        module: this.step.type,
                        input: this.step.message
                    });
                    this.step.addToSummary({
                      label: this.$t('mod.accommodation.message.summaryTitle'),
                      text: this.step.message
                    });
                }


            },
            clickNext() {
                if (this.validate()) {
                    this.$store.commit('setNextStep');
                } else {
                    this.showNextDisabledHint = true;
                }
            },
            clickPrev(e) {
                this.$store.commit('setLastStep');
            },
            addItem(index, reset) {
                this.showNextDisabledHint = false;
                if (reset) {
                    this.step.resetItems();
                }
                this.step.addItem(0, null, index)
            },
            removeItem(index) {
                this.showNextDisabledHint = false;
                this.step.removeItem(index);
                if (this.step.items.length === 0) {
                    this.step.configuratorActive = false;
                }
            },

        },
        components: {
          ContentHeadline,
          PeopleConfigurator,
        },
    }
</script>